import * as styles from "./DataTableCell.css";

import type { JSX } from "react";

import {
  type TooltipHandle,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  type Cell,
  type RowData,
  type SortingState,
  flexRender,
} from "@tanstack/react-table";

import type { DataTableSize, DataTableSpacing } from "./DataTable.types";
import { dataTableSpacingValue } from "./cellSizeStyles";
import type { dataTableFeatures } from "./dataTableFeatures";
import { useIsOverflowing } from "./useIsOverflowing";

interface DataTableCellProps<TData extends RowData> {
  cell: Cell<typeof dataTableFeatures, TData, unknown>;
  sorting: SortingState;
  tooltipHandle: TooltipHandle<string>;
  size: DataTableSize;
  columnBorders?: boolean;
  sticky?: boolean;
  stickyInset?: string;
  horizontalSpacing: DataTableSpacing;
  verticalSpacing: DataTableSpacing;
}

export function DataTableCell<TData extends RowData>({
  cell,
  sorting,
  tooltipHandle,
  size,
  columnBorders,
  sticky,
  stickyInset,
  horizontalSpacing,
  verticalSpacing,
}: DataTableCellProps<TData>): JSX.Element {
  const truncate = cell.column.columnDef.meta?.truncate ?? false;
  const sorted = sorting.some((entry) => entry.id === cell.column.id);

  const { containerRef, contentRef, isOverflowing } =
    useIsOverflowing<HTMLDivElement>(cell.getValue());

  const value = cell.getValue();
  const text =
    typeof value === "string" || typeof value === "number" ? String(value) : "";

  const content = (
    <div
      ref={contentRef}
      className={styles.content({ size, truncate })}
      style={{
        paddingInline: dataTableSpacingValue[horizontalSpacing],
        paddingBlock: dataTableSpacingValue[verticalSpacing],
      }}
      data-overflowing={truncate && isOverflowing ? "" : undefined}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  );

  return (
    <div
      role="cell"
      ref={containerRef}
      className={styles.cell({ size, columnBorders, sorted, sticky })}
      style={sticky && stickyInset ? { left: stickyInset } : undefined}
    >
      {truncate && isOverflowing && text ? (
        <TooltipTrigger
          handle={tooltipHandle}
          payload={text}
          render={content}
        />
      ) : (
        content
      )}
    </div>
  );
}
